import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Crown } from 'lucide-react';
import type { SegmentStatistics } from '@/types/statistics';
import { SHOW_WINNERS } from '@/lib/constants';
import type { PlayerColor } from './playerColors';
import LockedPreview from './LockedPreview';
import { EntityAvatar } from './EntityAvatar';
import {
  errorRateSeries,
  winnerRateSeries,
  winnersPerErrorSeries,
  type PlayerSeries,
} from './contributionMetrics';

const W = 320;
const H = 170;
const PAD = { l: 26, r: 6, t: 8, b: 20 };

/** A round step that splits `span` into at most `count` intervals. */
const niceStep = (span: number, count: number, steps: number[]) =>
  steps.find((s) => span / s <= count) ?? steps[steps.length - 1];

const sign = (n: number) => (n > 0 ? `+${n}` : `${n}`.replace('-', '−'));

/**
 * A running figure across the block, one step line per player (see
 * PlayerSeries): flat until the shot that changes it, a jump there, flat to the
 * end of the block after the last one. The viewer's line is drawn on top.
 */
export const StepChart: React.FC<{
  data: PlayerSeries;
  playerIds: string[];
  colors: Record<string, PlayerColor>;
  focusPlayerId: string;
  /** What the chart shows, for screen readers. */
  label: string;
  /** How a value-axis tick is written (as is by default). */
  format?: (value: number) => string;
}> = ({ data, playerIds, colors, focusPlayerId, label, format = String }) => {
  const { t } = useTranslation('tournament');
  const { series, duration } = data;

  const values = playerIds.flatMap(
    (id) => series[id]?.map((p) => p.value) ?? [],
  );
  const lo = Math.min(...values, 0);
  const hi = Math.max(...values, 0);
  const yStep = niceStep(
    hi - lo,
    4,
    [0.1, 0.2, 0.25, 0.5, 1, 2, 5, 10, 20, 25, 50, 100, 200, 500],
  );
  const yLo = Math.floor(lo / yStep) * yStep;
  const yHi = Math.max(Math.ceil(hi / yStep) * yStep, yLo + yStep);
  const minutes = duration / 60;
  const xStep = niceStep(minutes, 6, [1, 2, 5, 10, 15, 30, 60]);

  const x = (seconds: number) =>
    PAD.l + (seconds / (duration || 1)) * (W - PAD.l - PAD.r);
  const y = (value: number) =>
    PAD.t + ((yHi - value) / (yHi - yLo)) * (H - PAD.t - PAD.b);
  const yTicks = Array.from(
    { length: Math.round((yHi - yLo) / yStep) + 1 },
    // Rounded: steps like 0.1 add up with float noise.
    (_, i) => parseFloat((yLo + i * yStep).toFixed(2)),
  );
  const xTicks = Array.from(
    { length: Math.floor(minutes / xStep) + 1 },
    (_, i) => i * xStep,
  );
  const drawOrder = [
    ...playerIds.filter((id) => id !== focusPlayerId),
    ...playerIds.filter((id) => id === focusPlayerId),
  ];
  const steps = (line: PlayerSeries['series'][string]) => {
    const out: string[] = [];
    line.forEach((p, i) => {
      if (i > 0)
        out.push(`${x(p.t).toFixed(1)},${y(line[i - 1].value).toFixed(1)}`);
      out.push(`${x(p.t).toFixed(1)},${y(p.value).toFixed(1)}`);
    });
    const last = line[line.length - 1];
    if (last) out.push(`${x(duration).toFixed(1)},${y(last.value).toFixed(1)}`);
    return out.join(' ');
  };

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className="block w-full"
      role="img"
      aria-label={label}
    >
      {yTicks.map((v) => (
        <g key={v}>
          <line
            x1={PAD.l}
            x2={W - PAD.r}
            y1={y(v)}
            y2={y(v)}
            stroke={v === 0 ? 'var(--muted-2)' : 'var(--hairline)'}
            strokeWidth={v === 0 ? 1 : 0.6}
          />
          <text
            x={PAD.l - 5}
            y={y(v) + 3}
            textAnchor="end"
            fontSize={8.5}
            fill="var(--muted)"
            style={{ fontFamily: 'var(--f-label)' }}
          >
            {format(v)}
          </text>
        </g>
      ))}
      {xTicks.map((m) => (
        <text
          key={m}
          x={x(m * 60)}
          y={H - 6}
          textAnchor={m === 0 ? 'start' : 'middle'}
          fontSize={8.5}
          fill="var(--muted)"
          style={{ fontFamily: 'var(--f-label)' }}
        >
          {m === 0 ? `0 ${t('ring.min')}` : m}
        </text>
      ))}
      {drawOrder.map((id) => (
        <polyline
          key={id}
          points={steps(series[id] ?? [])}
          fill="none"
          stroke={colors[id]?.solid ?? 'var(--accent)'}
          strokeWidth={id === focusPlayerId ? 2.4 : 1.4}
          strokeLinejoin="miter"
          strokeLinecap="butt"
        />
      ))}
    </svg>
  );
};

/** Where `id`'s line ends: their figure at the end of the block. */
const finalValue = (data: PlayerSeries, id: string) => {
  const line = data.series[id] ?? [];
  return line.length ? line[line.length - 1].value : 0;
};

/** Each step's height by place (1st..4th). */
const STEP_HEIGHTS = [92, 74, 58, 44];

/**
 * Where everyone finished: a podium step per player, in the header's order,
 * as tall as their place and numbered with it; their photo stands on it with
 * their figure under it, and a crown on the best (none when all are level).
 * Players level share a place; `lowerIsBetter` ranks the lowest first.
 */
export const Podium: React.FC<{
  data: PlayerSeries;
  playerIds: string[];
  colors: Record<string, PlayerColor>;
  crops: Record<string, string>;
  lowerIsBetter?: boolean;
  /** How a figure is written over the photo. */
  format?: (value: number) => string;
}> = ({
  data,
  playerIds,
  colors,
  crops,
  lowerIsBetter = false,
  format = sign,
}) => {
  const finals = playerIds.map((id) => ({ id, final: finalValue(data, id) }));
  const values = finals.map((p) => p.final);
  const best = lowerIsBetter ? Math.min(...values) : Math.max(...values);
  const level = Math.min(...values) === Math.max(...values);
  const place = (final: number) =>
    finals.filter((p) => (lowerIsBetter ? p.final < final : p.final > final))
      .length + 1;

  return (
    <div
      className="score-podium"
      style={{
        gridTemplateColumns: `repeat(${finals.length}, minmax(0, 1fr))`,
      }}
    >
      {finals.map(({ id, final }, i) => {
        const color = colors[id]?.solid ?? 'var(--accent)';
        const rank = place(final);
        return (
          // Each step stacks over the one to its left, which hides that
          // one's side face wherever this step stands taller.
          <div key={id} className="score-podium-col" style={{ zIndex: i }}>
            <span className="score-podium-avatar">
              <span
                className="score-podium-photo"
                style={{ boxShadow: `0 0 0 3px ${color}` }}
              >
                {crops[id] ? <img src={crops[id]} alt="" /> : null}
              </span>
              {/* Worn by the best, on the photo's top-right edge. */}
              {final === best && !level ? (
                <Crown
                  className="score-podium-crown"
                  size={20}
                  strokeWidth={2.2}
                  aria-hidden="true"
                />
              ) : null}
            </span>
            <span className="score-podium-value">{format(final)}</span>
            <span
              className="score-podium-step"
              style={
                {
                  height: STEP_HEIGHTS[rank - 1],
                  '--step': color,
                } as React.CSSProperties
              }
            >
              <span className="score-podium-rank">{rank}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
};

/**
 * Where each one's line ended, in one compact row under a chart: their avatar
 * (a team's members) and the figure. The chart says who did better.
 */
export const RaceFinals: React.FC<{
  data: PlayerSeries;
  playerIds: string[];
  colors: Record<string, PlayerColor>;
  crops: Record<string, string>;
  names: Record<string, string>;
  memberCrops?: Record<string, string[] | undefined>;
  format: (value: number) => string;
}> = ({ data, playerIds, colors, crops, names, memberCrops, format }) => (
  <div className="race-finals">
    {playerIds.map((id) => (
      <span key={id} className="race-final">
        <EntityAvatar
          name={names[id] ?? id}
          color={colors[id]?.solid ?? 'var(--accent)'}
          crop={crops[id] || undefined}
          crops={memberCrops?.[id]}
        />
        <span className="race-final-value">{format(finalValue(data, id))}</span>
      </span>
    ))}
  </div>
);

/**
 * Error rate, winner rate and winners per error, each run shot by shot across
 * the block for `playerIds` (players, or teams aggregated as one), with where
 * they finished: on a podium (from two of them), or, given `names` (In Depth),
 * as a compact row of final figures. On the podium the lowest error rate places
 * first; the highest winner rate and winners per error do. `shotType` narrows
 * it to one shot chip.
 */
export const ShotRaces: React.FC<{
  segment: SegmentStatistics;
  playerIds: string[];
  colors: Record<string, PlayerColor>;
  crops: Record<string, string>;
  focusPlayerId?: string;
  shotType?: string;
  /** Final figures in a compact row, with these names, instead of a podium. */
  names?: Record<string, string>;
  /** A team's members' crops, for its avatar in the compact row. */
  memberCrops?: Record<string, string[] | undefined>;
  /** The guided tour's handle on the three charts. */
  tour?: string;
}> = ({
  segment,
  playerIds,
  colors,
  crops,
  focusPlayerId = '',
  shotType = 'all',
  names,
  memberCrops,
  tour,
}) => {
  const { t } = useTranslation('tournament');
  // Axis ticks are round; the podium's rates carry a decimal.
  const percent = (v: number) => `${Math.round(v)}%`;
  const rate = (v: number) => `${v.toFixed(1)}%`;
  const races: {
    key: 'errorRate' | 'winnerRate' | 'winnersPerError';
    data: PlayerSeries | null;
    lowerIsBetter?: boolean;
    tick?: (v: number) => string;
    format: (v: number) => string;
  }[] = [
    {
      key: 'errorRate',
      data: errorRateSeries(segment, playerIds, shotType),
      lowerIsBetter: true,
      tick: percent,
      format: rate,
    },
    ...(SHOW_WINNERS
      ? [
          {
            key: 'winnerRate' as const,
            data: winnerRateSeries(segment, playerIds, shotType),
            tick: percent,
            format: rate,
          },
          {
            key: 'winnersPerError' as const,
            data: winnersPerErrorSeries(segment, playerIds, shotType),
            format: (v: number) => v.toFixed(1),
          },
        ]
      : []),
  ];

  return (
    <div data-tour={tour}>
      {races.map(({ key, data, lowerIsBetter, tick, format }) =>
        data ? (
          <div key={key} className="contribution-section">
            <div className="contribution-q">
              {t(`overview.contribution.${key}`)}
            </div>
            <StepChart
              data={data}
              playerIds={playerIds}
              colors={colors}
              focusPlayerId={focusPlayerId}
              label={t(`overview.contribution.${key}`)}
              format={tick}
            />
            {names ? (
              <RaceFinals
                data={data}
                playerIds={playerIds}
                colors={colors}
                crops={crops}
                names={names}
                memberCrops={memberCrops}
                format={format}
              />
            ) : playerIds.length > 1 ? (
              <Podium
                data={data}
                playerIds={playerIds}
                colors={colors}
                crops={crops}
                lowerIsBetter={lowerIsBetter}
                format={format}
              />
            ) : null}
          </div>
        ) : null,
      )}
    </div>
  );
};

/**
 * The block's Matchlytics Score, laid out like the court's "You" section that
 * follows it: how every player's accepted shots added up, shot by shot (see
 * matchlyticsScore), then a podium of where they finished. `locked` (a basic
 * report) shows a sample podium behind fog, without the lines.
 */
const MatchlyticsScoreSection: React.FC<{
  score: PlayerSeries;
  /** The players in the roster's order. */
  playerIds: string[];
  colors: Record<string, PlayerColor>;
  crops: Record<string, string>;
  focusPlayerId: string;
  locked?: boolean;
}> = ({ score, playerIds, colors, crops, focusPlayerId, locked = false }) => {
  const { t } = useTranslation('tournament');
  const podium = (
    <Podium data={score} playerIds={playerIds} colors={colors} crops={crops} />
  );

  return (
    <section className="score-section" data-tour="score">
      <div className="court-header">
        <div className="court-header-left">
          {/* MATCH SCORE, signed "by" the Matchlytics logo. */}
          <div className="score-title">
            <span className="court-header-name">
              {t('score.title').toUpperCase()}
            </span>
            <span className="score-by">
              {t('score.by')}
              <span className="score-by-logo">
                <img src="/icons/logo_with_name.png" alt="Matchlytics" />
              </span>
            </span>
          </div>
          <div className="court-header-sub">{t('score.subtitle')}</div>
        </div>
      </div>
      {/* Locked, the podium alone is the pitch: the lines are the product. */}
      {locked ? (
        <LockedPreview hook={t('locked.hook.score')}>
          {/* A card, like every locked sample: it joins the pitch above it. */}
          <div className="card">{podium}</div>
        </LockedPreview>
      ) : (
        <>
          <StepChart
            data={score}
            playerIds={playerIds}
            colors={colors}
            focusPlayerId={focusPlayerId}
            label={t('score.title')}
          />
          {podium}
        </>
      )}
    </section>
  );
};

export default MatchlyticsScoreSection;
